import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { conversionsApi } from '../../services/api';
import './Conversions.css';

const ConversionList = () => {
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, rejected
  const [dateRange, setDateRange] = useState('week'); // week, month, all

  useEffect(() => {
    const fetchConversions = async () => {
      try {
        setLoading(true);
        
        // Prepare API params
        const params = {};
        if (filter !== 'all') {
          params.status = filter;
        }
        
        // Add date range
        const now = new Date();
        if (dateRange === 'week') {
          const weekAgo = new Date();
          weekAgo.setDate(now.getDate() - 7);
          params.startDate = weekAgo.toISOString();
        } else if (dateRange === 'month') {
          const monthAgo = new Date();
          monthAgo.setDate(now.getDate() - 30);
          params.startDate = monthAgo.toISOString();
        }
        // If 'all' is selected, we don't add a date filter
        
        if (params.startDate) {
          params.endDate = now.toISOString();
        }
        
        // Fetch conversions from API
        const response = await conversionsApi.getConversions(params);
        
        if (response.data.status === 'success') {
          // Access the conversions array from the new standardized response structure
          setConversions(response.data.data.conversions || []);
        } else {
          throw new Error('Failed to fetch conversions');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching conversions:', err);
        setError('Failed to load conversions');
        setLoading(false);
      }
    };

    fetchConversions();
  }, [filter, dateRange]);

  const getFilteredConversions = () => {
    let filtered = [...conversions];
    
    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(conv => conv.status === filter);
    }
    
    // Apply date filter
    if (dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      if (dateRange === 'week') {
        cutoff.setDate(now.getDate() - 7);
      } else if (dateRange === 'month') {
        cutoff.setDate(now.getDate() - 30);
      }
      
      filtered = filtered.filter(conv => new Date(conv.timestamp || conv.createdAt) >= cutoff);
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading conversions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }

  const filteredConversions = getFilteredConversions();

  return (
    <div className="conversion-list-page">
      <div className="page-header">
        <h1>Conversions</h1>
      </div>
      
      <div className="filters-container">
        <div className="filter-group">
          <label>Status:</label>
          <div className="btn-group">
            <button 
              className={`btn-filter ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={`btn-filter ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
            <button 
              className={`btn-filter ${filter === 'confirmed' ? 'active' : ''}`}
              onClick={() => setFilter('confirmed')}
            >
              Confirmed
            </button>
            <button 
              className={`btn-filter ${filter === 'rejected' ? 'active' : ''}`}
              onClick={() => setFilter('rejected')}
            >
              Rejected
            </button>
          </div>
        </div>
        
        <div className="filter-group">
          <label>Time Period:</label>
          <div className="btn-group">
            <button 
              className={`btn-filter ${dateRange === 'week' ? 'active' : ''}`}
              onClick={() => setDateRange('week')}
            >
              Last 7 Days
            </button>
            <button 
              className={`btn-filter ${dateRange === 'month' ? 'active' : ''}`}
              onClick={() => setDateRange('month')}
            >
              Last 30 Days
            </button>
            <button 
              className={`btn-filter ${dateRange === 'all' ? 'active' : ''}`}
              onClick={() => setDateRange('all')}
            >
              All Time
            </button>
          </div>
        </div>
      </div>
      
      {filteredConversions.length === 0 ? (
        <div className="empty-container">
          <h2>No Conversions Found</h2>
          <p>There are no conversions matching your selected filters.</p>
          <button className="btn" onClick={() => { setFilter('all'); setDateRange('all'); }}>
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="conversions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Link</th>
                <th>Amount</th>
                <th>Source</th>
                <th>Transaction ID</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredConversions.map(conversion => (
                <tr key={conversion.id}>
                  <td>
                    {new Date(conversion.createdAt || conversion.timestamp).toLocaleDateString()}
                    <div className="time">
                      {new Date(conversion.createdAt || conversion.timestamp).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>
                    <Link to={`/links/${conversion.linkId}`}>
                      {conversion.linkName || "Link"}
                    </Link>
                  </td>
                  <td className="amount">
                    {conversion.currency === 'USD' ? '$' : ''}{(conversion.amount || conversion.value || 0).toFixed(2)}
                  </td>
                  <td>
                    <span className={`source-badge ${conversion.source}`}>
                      {conversion.source || "website"}
                    </span>
                  </td>
                  <td className="transaction-id">
                    {conversion.transactionId || conversion.orderId || '-'}
                  </td>
                  <td>
                    <span className={`status-badge ${conversion.status || 'confirmed'}`}>
                      {conversion.status || 'confirmed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="export-link-container">
        <Link to="/dashboard/export" className="btn">
          Export Conversion Data
        </Link>
      </div>
    </div>
  );
};

export default ConversionList;
